import type { Item } from '../types';

export const DAMAGE_MARKERS = ['schaden', 'damage', 'angriff', 'attack'];
export const MOVEMENT_MARKERS = ['bewegung', 'movement', 'speed'];
export const RANGE_MARKERS = ['reichweite', 'range'];
export const ARMOR_MARKERS = ['rüstungswert', 'ruestungswert', 'rüstung', 'ruestung', 'armor'];
export const HEAL_MARKERS = ['heilung', 'heil', 'heal', 'leben', 'hp', 'lp', 'restore'];
export const MANA_MARKERS = ['mana', 'mp'];
export const POTION_TYPES = ['trank', 'consumable', 'potion', 'verbrauch'];
export const DEFAULT_ARMOR_FORMULA = 'max(0, schaden - ruestung)';

export type PotionRestore = {
  health: number;
  mana: number;
  healthPercent: number;
  manaPercent: number;
};

export type ResolvedPotion = {
  health: number;
  mana: number;
  healthLabel: string;
  manaLabel: string;
};

export type ItemEffectModifier = {
  label: string;
  snippet: string;
  hint: string;
  icon: string;
};

/** Effekt-Tags, die der Admin beim Item-Anlegen setzen kann. */
export const ITEM_EFFECT_MODIFIERS: ItemEffectModifier[] = [
  {
    label: '+1 Schaden',
    snippet: '+1 Schaden',
    hint: 'Erhöht den Angriffsschaden',
    icon: '⚔',
  },
  {
    label: '+1 Bewegung',
    snippet: '+1 Bewegung',
    hint: 'Erhöht die Bewegungspunkte',
    icon: '➤',
  },
  {
    label: '+1 Reichweite',
    snippet: '+1 Reichweite',
    hint: 'Wird auf der Item-Karte angezeigt',
    icon: '🏹',
  },
  {
    label: '+1 Rüstung',
    snippet: '+1 Rüstung',
    hint: 'Zusatzleben. Schaden geht zuerst auf Buffs, dann auf diese Rüstung, dann auf Leben',
    icon: '🛡',
  },
  {
    label: 'Heilung 5',
    snippet: 'Heilung 5',
    hint: 'Stellt beim Trinken feste Lebenspunkte wieder her',
    icon: '💚',
  },
  {
    label: 'Heilung 25%',
    snippet: 'Heilung 25%',
    hint: 'Heilt einen Anteil vom Max-Leben, ausgerechnet im Charakterbogen',
    icon: '💚',
  },
  {
    label: 'Mana 5',
    snippet: 'Mana 5',
    hint: 'Stellt beim Trinken feste Manapunkte wieder her',
    icon: '✨',
  },
  {
    label: 'Mana 25%',
    snippet: 'Mana 25%',
    hint: 'Stellt einen Anteil vom Max-Mana wieder her, ausgerechnet im Charakterbogen',
    icon: '✨',
  },
];

export const ITEM_STAT_MODIFIERS = [
  { key: 'strength_bonus' as const, icon: '💪', label: 'Stärke', hint: 'Zählt direkt zum Schaden' },
  { key: 'dexterity_bonus' as const, icon: '🎯', label: 'Geschick', hint: 'Anzeige auf der Item-Karte' },
  { key: 'intelligence_bonus' as const, icon: '🧠', label: 'Intelligenz', hint: 'Anzeige auf der Item-Karte' },
  { key: 'constitution_bonus' as const, icon: '❤️', label: 'Konstitution', hint: 'Anzeige auf der Item-Karte' },
  { key: 'health_bonus' as const, icon: '💚', label: 'Leben', hint: 'Flaches Max-Leben, zusätzlich zur Konstitution' },
  { key: 'damage_bonus' as const, icon: '⚔', label: 'Schaden', hint: 'Zählt direkt zum Schaden' },
];

/** Werte, die ein Admin-Custom-Buff ändern kann (wie dauerhafte Trank-Effekte). */
export const BUFF_BONUS_FIELDS = [
  { key: 'damage' as const, icon: '⚔', label: 'Schaden' },
  { key: 'movement' as const, icon: '➤', label: 'Bewegung' },
  { key: 'range' as const, icon: '🏹', label: 'Reichweite' },
  { key: 'armor' as const, icon: '🛡', label: 'Rüstung' },
  { key: 'health' as const, icon: '💚', label: 'Leben' },
  { key: 'mana' as const, icon: '✨', label: 'Mana' },
];

export type BuffBonusKey = (typeof BUFF_BONUS_FIELDS)[number]['key'] | 'heal' | 'heal_count';

export type BuffBonuses = Partial<Record<BuffBonusKey, number>>;

export function formatBuffEffect(bonuses: BuffBonuses | null | undefined): string {
  const parts: string[] = [];
  for (const field of BUFF_BONUS_FIELDS) {
    const value = Math.round(Number(bonuses?.[field.key]) || 0);
    if (!value) continue;
    const sign = value > 0 ? '+' : '';
    parts.push(`${sign}${value} ${field.label}`);
  }
  const heal = Math.round(Number(bonuses?.heal) || 0);
  const count = Math.round(Number(bonuses?.heal_count) || 0);
  if (heal > 0) {
    const uses = count > 0 ? count : 1;
    parts.push(`Heilung ${heal} × ${uses}`);
  }
  return parts.join(', ');
}

export function signedNumber(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const SIGNED_VITAL_RE = /([+-]\d+)\s*(?:leben|mana)\b|\b(?:leben|mana)\s*[:+]\s*([+-]?\d+)/gi;

/** +5 Leben / Mana +3 sind Max-Werte, keine Trank-Heilung (die heißt Heilung 5 oder Mana 5). */
export function stripSignedVitals(text: string): string {
  return text.replace(SIGNED_VITAL_RE, ' ');
}

export function parseSignedStat(text: string | undefined, marker: string): number {
  if (!text) return 0;
  const pattern = new RegExp(
    `([+-]\\d+)\\s*${escapeRegExp(marker)}\\b|\\b${escapeRegExp(marker)}\\s*[:+]\\s*([+-]?\\d+)`,
    'gi',
  );
  let total = 0;
  let match = pattern.exec(text);
  while (match) {
    total += parseInt(match[1] || match[2] || '0', 10);
    match = pattern.exec(text);
  }
  return total;
}

export function parseEffectBonus(text: string | undefined, markers: string[]): number {
  return parseMarkerAmounts(text, markers).flat;
}

function parseMarkerAmounts(text: string | undefined, markers: string[]): { flat: number; percent: number } {
  if (!text) return { flat: 0, percent: 0 };
  const blob = text.toLowerCase();
  let flat = 0;
  let percent = 0;
  for (const marker of markers) {
    const escaped = escapeRegExp(marker);
    const percentPattern = new RegExp(
      `(\\d+(?:[.,]\\d+)?)\\s*(?:%|prozent)\\s*${escaped}\\b|\\b${escaped}\\s*[:+]?\\s*(\\d+(?:[.,]\\d+)?)\\s*(?:%|prozent)`,
      'gi',
    );
    let match = percentPattern.exec(blob);
    while (match) {
      percent += parseFloat((match[1] || match[2] || '0').replace(',', '.'));
      match = percentPattern.exec(blob);
    }
    const flatPattern = new RegExp(
      `([+-]?\\d+)(?!\\d)(?!\\s*(?:%|prozent))\\s*${escaped}\\b|\\b${escaped}\\s*[:+]?\\s*([+-]?\\d+)(?!\\d)(?!\\s*(?:%|prozent))`,
      'gi',
    );
    match = flatPattern.exec(blob);
    while (match) {
      flat += parseInt(match[1] || match[2] || '0', 10);
      match = flatPattern.exec(blob);
    }
  }
  return { flat, percent };
}

function tidyNumber(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

export function appendEffectTag(current: string, snippet: string): string {
  const trimmed = (current || '').trim();
  const plusMatch = snippet.match(/^\+(\d+)\s+(\S+)$/);
  if (plusMatch) {
    const amount = parseInt(plusMatch[1], 10);
    const marker = plusMatch[2];
    const pattern = new RegExp(`(\\+)(\\d+)(\\s+${escapeRegExp(marker)})`, 'i');
    if (trimmed && pattern.test(trimmed)) {
      return trimmed.replace(pattern, (_all, plus, digits, rest) => (
        `${plus}${parseInt(digits, 10) + amount}${rest}`
      ));
    }
  }
  const percentMatch = snippet.match(/^(\S+)\s+(\d+(?:[.,]\d+)?)%$/);
  if (percentMatch) {
    const marker = percentMatch[1];
    const amount = parseFloat(percentMatch[2].replace(',', '.'));
    const pattern = new RegExp(`(${escapeRegExp(marker)})\\s+(\\d+(?:[.,]\\d+)?)%`, 'i');
    if (trimmed && pattern.test(trimmed)) {
      return trimmed.replace(pattern, (_all, word, digits) => {
        const next = parseFloat(String(digits).replace(',', '.')) + amount;
        return `${word} ${tidyNumber(next)}%`;
      });
    }
  } else if (trimmed.toLowerCase().includes(snippet.toLowerCase())) {
    return trimmed;
  }
  return trimmed ? `${trimmed}, ${snippet}` : snippet;
}

export function hasDamageText(...parts: Array<string | undefined>): boolean {
  const blob = parts.filter(Boolean).join(' ').toLowerCase();
  return DAMAGE_MARKERS.some(marker => blob.includes(marker));
}

export function hasGearText(...parts: Array<string | undefined>): boolean {
  const blob = parts.filter(Boolean).join(' ').toLowerCase();
  return [...DAMAGE_MARKERS, ...MOVEMENT_MARKERS, ...RANGE_MARKERS, ...ARMOR_MARKERS].some(marker => blob.includes(marker));
}

export function isPotionType(type?: string): boolean {
  return POTION_TYPES.includes((type || '').toLowerCase());
}

const EMPTY_POTION: PotionRestore = { health: 0, mana: 0, healthPercent: 0, manaPercent: 0 };

export function itemPotionRestore(
  item: Pick<Item, 'name' | 'type' | 'description' | 'effect'>,
): PotionRestore {
  if (hasDamageText(item.name, item.type, item.description, item.effect)) {
    return { ...EMPTY_POTION };
  }
  const tagged = stripSignedVitals(`${item.name || ''} ${item.effect || ''}`);
  const healthParts = parseMarkerAmounts(tagged, HEAL_MARKERS);
  const manaParts = parseMarkerAmounts(tagged, MANA_MARKERS);
  let health = healthParts.flat;
  let mana = manaParts.flat;
  const healthPercent = healthParts.percent;
  const manaPercent = manaParts.percent;
  const leftover = parseInt((item.effect || '').match(/\d+/)?.[0] || '0', 10) || 0;
  const parts = [item.name, item.type, item.description, item.effect];
  if (
    leftover
    && health === 0
    && mana === 0
    && healthPercent === 0
    && manaPercent === 0
    && !hasGearText(...parts)
  ) {
    const blob = parts.filter(Boolean).join(' ').toLowerCase();
    if (MANA_MARKERS.some(marker => blob.includes(marker))) {
      mana = leftover;
    } else if (isPotionType(item.type) || HEAL_MARKERS.some(marker => blob.includes(marker))) {
      health = leftover;
    }
  }
  return { health, mana, healthPercent, manaPercent };
}

export function resolvePotionValue(flat: number, percent: number, maximum: number): number {
  const cap = Math.max(0, Number(maximum) || 0);
  const fromPercent = percent ? Math.round((cap * percent) / 100) : 0;
  return Math.max(0, Math.round(flat) + fromPercent);
}

function potionAmountLabel(kind: string, flat: number, percent: number, resolved: number, maximum: number): string {
  if (resolved <= 0) return '';
  if (percent > 0 && flat > 0) {
    return `+${resolved} ${kind} (${tidyNumber(flat)} + ${tidyNumber(percent)}% von ${maximum})`;
  }
  if (percent > 0) {
    return `+${resolved} ${kind} (${tidyNumber(percent)}% von ${maximum})`;
  }
  return `+${resolved} ${kind}`;
}

export function potionCatalogLabel(flat: number, percent: number): string {
  if (percent > 0 && flat > 0) return `${tidyNumber(flat)}+${tidyNumber(percent)}%`;
  if (percent > 0) return `${tidyNumber(percent)}%`;
  if (flat > 0) return String(flat);
  return '';
}

export function potionRestoreSummary(
  item: Pick<Item, 'name' | 'type' | 'description' | 'effect'>,
  maxHealth: number,
  maxMana: number,
): ResolvedPotion {
  const raw = itemPotionRestore(item);
  const health = resolvePotionValue(raw.health, raw.healthPercent, maxHealth);
  const mana = resolvePotionValue(raw.mana, raw.manaPercent, maxMana);
  return {
    health,
    mana,
    healthLabel: potionAmountLabel('Leben', raw.health, raw.healthPercent, health, maxHealth),
    manaLabel: potionAmountLabel('Mana', raw.mana, raw.manaPercent, mana, maxMana),
  };
}

export function potionRestoresHealth(
  item: Pick<Item, 'name' | 'type' | 'description' | 'effect'>,
): boolean {
  const restore = itemPotionRestore(item);
  return restore.health > 0 || restore.healthPercent > 0;
}

export function isPotionItem(
  item: Pick<Item, 'name' | 'type' | 'description' | 'effect'>,
): boolean {
  const restore = itemPotionRestore(item);
  return restore.health > 0 || restore.mana > 0 || restore.healthPercent > 0 || restore.manaPercent > 0;
}

export function isConsumablePotion(
  item: Pick<Item, 'name' | 'type' | 'description' | 'effect'>,
): boolean {
  return isPotionType(item.type) || isPotionItem(item);
}

export function potionHasLastingEffect(
  item: Pick<Item, 'name' | 'type' | 'description' | 'effect' | 'strength_bonus' | 'dexterity_bonus' | 'intelligence_bonus' | 'constitution_bonus' | 'health_bonus' | 'damage_bonus'>,
): boolean {
  if (!isPotionType(item.type)) return false;
  const effect = (item.effect || '').trim();
  if (
    (item.strength_bonus || 0)
    || (item.dexterity_bonus || 0)
    || (item.intelligence_bonus || 0)
    || (item.constitution_bonus || 0)
    || (item.health_bonus || 0)
    || (item.damage_bonus || 0)
  ) {
    return true;
  }
  if (!effect) return false;
  if (isPotionItem(item)) {
    return hasGearText(item.effect, item.description);
  }
  return true;
}

const GOLD_NAME = /^(gold(m[uü]nzen?)?|m[uü]nzen?)$/i;
const GOLD_TYPE = /^(gold|m[uü]nze|w[aä]hrung|currency)$/i;
const ESSENCE_NAME = /^(?:naturessenz(?:en)?|naturesenz(?:en)?|natur[\s-]essenz(?:en)?|naturessen)$/i;
const ESSENCE_TYPE = /^(?:naturessenz|natur[\s-]?essenz)$/i;

export function isGoldItem(
  item: Pick<Item, 'name' | 'type'>,
): boolean {
  return GOLD_NAME.test((item.name || '').trim()) || GOLD_TYPE.test((item.type || '').trim());
}

export function isEssenceItem(
  item: Pick<Item, 'name' | 'type'>,
): boolean {
  return ESSENCE_NAME.test((item.name || '').trim()) || ESSENCE_TYPE.test((item.type || '').trim());
}

export function isCurrencyItem(
  item: Pick<Item, 'name' | 'type'>,
): boolean {
  return isGoldItem(item) || isEssenceItem(item);
}

export function formatItemCost(item: { price?: number | null; essence_price?: number | null }): string {
  const gold = Math.max(0, Number(item.price) || 0);
  const essence = Math.max(0, Number(item.essence_price) || 0);
  const parts: string[] = [];
  if (gold > 0) parts.push(`💰${gold}`);
  if (essence > 0) parts.push(`🍃${essence}`);
  return parts.join(' ');
}

export function isStackableItem(
  item: Pick<Item, 'name' | 'type' | 'description' | 'effect'>,
): boolean {
  return isConsumablePotion(item) || isCurrencyItem(item);
}

export type ItemStack<T extends Pick<Item, 'id' | 'name' | 'type' | 'description' | 'effect'>> = {
  item: T;
  count: number;
};

export function stackInventoryItems<T extends Pick<Item, 'id' | 'name' | 'type' | 'description' | 'effect'>>(
  items: T[] | null | undefined,
): ItemStack<T>[] {
  const stacks: ItemStack<T>[] = [];
  const stackIndex = new Map<number, number>();
  for (const item of items || []) {
    if (isStackableItem(item) && stackIndex.has(item.id)) {
      stacks[stackIndex.get(item.id)!].count += 1;
      continue;
    }
    stacks.push({ item, count: 1 });
    if (isStackableItem(item)) stackIndex.set(item.id, stacks.length - 1);
  }
  return stacks;
}

export function stackPotionItems<T extends Pick<Item, 'id' | 'name' | 'type' | 'description' | 'effect'>>(
  items: T[] | null | undefined,
): ItemStack<T>[] {
  return stackInventoryItems(items);
}

/** Im Inventar ohne Kampfbonus; wirken erst als aktiver/Custom-Effekt (Stärketrank, Pferd, Würfel). */
const INVENTORY_EFFECT_ONLY_TYPES = new Set([
  'trank', 'consumable', 'potion', 'verbrauch',
  'sonstiges', 'token', 'brett',
]);

/** Ausrüstung ja. Tränke/Sonstiges (Pferd, Würfel) erst als aktiver Effekt. */
export function contributesCombatBonus(
  item: Pick<Item, 'name' | 'type' | 'description' | 'effect' | 'strength_bonus' | 'dexterity_bonus' | 'intelligence_bonus' | 'constitution_bonus'>,
): boolean {
  if (isCurrencyItem(item)) return false;
  const type = (item.type || '').trim().toLowerCase();
  if (type === 'effekt' || type === 'effect' || type === 'buff' || type === 'status') {
    return true;
  }
  if (INVENTORY_EFFECT_ONLY_TYPES.has(type) || isPotionItem(item)) {
    return false;
  }
  return true;
}

function gearItems(items?: Item[] | null): Item[] {
  return (items || []).filter(contributesCombatBonus);
}

export function bonusSources(character?: {
  items?: Item[] | null;
  effects?: Array<Partial<Item> & { effect?: string; description?: string; type?: string }> | null;
} | null): Item[] {
  return [
    ...gearItems(character?.items),
    ...(character?.effects || []).map(effect => ({
      id: effect.id || 0,
      name: effect.name || 'Effekt',
      type: effect.type || 'Effekt',
      description: effect.description || '',
      effect: effect.effect || '',
      icon_url: effect.icon_url,
      armor_remaining: effect.armor_remaining,
    })),
  ];
}

export function itemDamageBonus(items?: Item[] | null): number {
  return gearItems(items).reduce((sum, item) => (
    sum
    + (item.strength_bonus || 0)
    + (item.damage_bonus || 0)
    + parseEffectBonus(item.effect, DAMAGE_MARKERS)
  ), 0);
}

export function itemHealthBonus(items?: Item[] | null): number {
  return gearItems(items).reduce((sum, item) => (
    sum + (item.health_bonus || 0) + parseSignedStat(item.effect, 'leben')
  ), 0);
}

export function itemManaBonus(items?: Item[] | null): number {
  return gearItems(items).reduce((sum, item) => (
    sum + parseSignedStat(item.effect, 'mana')
  ), 0);
}

export function itemMovementBonus(items?: Item[] | null): number {
  return gearItems(items).reduce((sum, item) => (
    sum + parseEffectBonus(item.effect, MOVEMENT_MARKERS)
  ), 0);
}

export function itemRangeBonus(items?: Item[] | null): number {
  return gearItems(items).reduce((sum, item) => (
    sum + parseEffectBonus(item.effect, RANGE_MARKERS)
  ), 0);
}

export function itemArmorMax(item?: Partial<Item> | null): number {
  if (!item || !contributesCombatBonus(item as Item)) return 0;
  return Math.max(0, parseEffectBonus(`${item.effect || ''} ${item.description || ''}`, ARMOR_MARKERS));
}

export function itemArmorRemaining(item?: Partial<Item> | null): number {
  const maximum = itemArmorMax(item);
  if (maximum <= 0 || !item) return 0;
  if (item.armor_remaining === null || item.armor_remaining === undefined) return maximum;
  const remaining = Math.round(Number(item.armor_remaining) || 0);
  return Math.max(0, Math.min(maximum, remaining));
}

export function isArmorBroken(item?: Partial<Item> | null): boolean {
  if (!item || itemArmorMax(item) <= 0) return false;
  if (item.armor_remaining === null || item.armor_remaining === undefined) return false;
  return Number(item.armor_remaining) <= 0;
}

export function isArmorDamaged(item?: Partial<Item> | null): boolean {
  const maximum = itemArmorMax(item);
  return maximum > 0 && itemArmorRemaining(item) < maximum;
}

export function armorPool(items?: Array<Partial<Item>> | null): { current: number; max: number } {
  let current = 0;
  let max = 0;
  for (const item of items || []) {
    const remaining = itemArmorRemaining(item);
    const full = itemArmorMax(item);
    if (remaining <= 0 || full <= 0) continue;
    current += remaining;
    max += full;
  }
  return { current, max };
}

export function itemArmorBonus(items?: Item[] | null): number {
  return gearItems(items).reduce((sum, item) => sum + itemArmorRemaining(item), 0);
}

function evalArmorExpression(expression: string): number {
  const sanitized = expression.replace(/max/g, 'Math.max').replace(/min/g, 'Math.min');
  if (!/^[\d\s+\-*/().,A-Za-z]+$/.test(sanitized)) {
    return Number.NaN;
  }
  // eslint-disable-next-line no-new-func
  const result = Function('"use strict"; return (' + sanitized + ')')();
  return Number(result);
}

export function reduceDamageByArmor(
  incoming: number,
  armor: number,
  formula = DEFAULT_ARMOR_FORMULA,
): number {
  const schaden = Math.max(0, Math.round(Number(incoming) || 0));
  const ruestung = Math.max(0, Math.round(Number(armor) || 0));
  const source = (formula || DEFAULT_ARMOR_FORMULA).trim() || DEFAULT_ARMOR_FORMULA;
  const expression = source
    .toLowerCase()
    .replace(/rüstung/g, 'ruestung')
    .replace(/incoming/g, 'schaden')
    .replace(/armor/g, String(ruestung))
    .replace(/ruestung/g, String(ruestung))
    .replace(/schaden/g, String(schaden))
    .replace(/damage/g, String(schaden));
  try {
    const result = evalArmorExpression(expression);
    if (!Number.isFinite(result)) {
      return Math.max(0, schaden - ruestung);
    }
    return Math.max(0, Math.round(result));
  } catch {
    return Math.max(0, schaden - ruestung);
  }
}

export function totalAttackDamage(base: number, items?: Item[] | null): number {
  return Math.max(0, Math.round(base + itemDamageBonus(items)));
}

export function totalMovement(base: number, items?: Item[] | null): number {
  return Math.max(0, Math.round(base + itemMovementBonus(items)));
}

export function totalHealth(base: number, items?: Item[] | null): number {
  return Math.max(0, Math.round(base + itemHealthBonus(items)));
}

export function totalMana(base: number, items?: Item[] | null): number {
  return Math.max(0, Math.round(base + itemManaBonus(items)));
}

/** Volle Vorräte wachsen mit dem Item-Bonus mit, angeschlagene bleiben. */
export function vitalWithItemBonus(current: number, formulaMax: number, bonus: number): { current: number; max: number } {
  const max = Math.max(0, Math.round(formulaMax + bonus));
  let value = Math.max(0, Number(current) || 0);
  if (bonus > 0 && formulaMax > 0 && value >= formulaMax) value = max;
  if (max > 0) value = Math.min(value, max);
  return { current: value, max };
}

export function itemEffectTags(item: Pick<Item, 'name' | 'type' | 'description' | 'effect' | 'strength_bonus' | 'dexterity_bonus' | 'intelligence_bonus' | 'constitution_bonus' | 'health_bonus' | 'damage_bonus'>) {
  const restore = itemPotionRestore(item);
  const potion = isPotionItem(item);
  const effectDamage = potion ? 0 : parseEffectBonus(item.effect, DAMAGE_MARKERS);
  const effectMove = potion ? 0 : parseEffectBonus(item.effect, MOVEMENT_MARKERS);
  const effectRange = potion ? 0 : parseEffectBonus(item.effect, RANGE_MARKERS);
  const effectArmor = potion ? 0 : parseEffectBonus(`${item.effect || ''} ${item.description || ''}`, ARMOR_MARKERS);
  const statValues = [
    item.strength_bonus,
    item.dexterity_bonus,
    item.intelligence_bonus,
    item.constitution_bonus,
    item.health_bonus,
    item.damage_bonus,
  ];
  return {
    effectDamage,
    effectMove,
    effectRange,
    effectArmor,
    healthRestore: restore.health,
    manaRestore: restore.mana,
    healthPercent: restore.healthPercent,
    manaPercent: restore.manaPercent,
    isPotion: potion,
    hasAny: effectDamage !== 0
      || effectMove !== 0
      || effectRange !== 0
      || effectArmor !== 0
      || restore.health > 0
      || restore.mana > 0
      || restore.healthPercent > 0
      || restore.manaPercent > 0
      || (!potion && statValues.some(value => (value || 0) !== 0))
      || parseSignedStat(item.effect, 'leben') !== 0
      || parseSignedStat(item.effect, 'mana') !== 0,
  };
}

export type ItemChipKind = 'damage' | 'heal' | 'mana' | 'move' | 'range' | 'armor' | 'strength' | 'dexterity' | 'intelligence' | 'constitution' | 'life';

export type ItemChip = {
  kind: ItemChipKind;
  icon: string;
  label: string;
};

const ATTRIBUTE_CHIPS: Array<{
  key: 'strength_bonus' | 'dexterity_bonus' | 'intelligence_bonus' | 'constitution_bonus' | 'health_bonus' | 'damage_bonus';
  kind: ItemChipKind;
  icon: string;
  label: string;
}> = [
  { key: 'strength_bonus', kind: 'strength', icon: '💪', label: 'Stärke' },
  { key: 'dexterity_bonus', kind: 'dexterity', icon: '🎯', label: 'Geschick' },
  { key: 'intelligence_bonus', kind: 'intelligence', icon: '🧠', label: 'Intelligenz' },
  { key: 'constitution_bonus', kind: 'constitution', icon: '❤️', label: 'Konstitution' },
  { key: 'health_bonus', kind: 'life', icon: '💚', label: 'Leben' },
  { key: 'damage_bonus', kind: 'damage', icon: '⚔', label: 'Schaden' },
];

export function itemKitChips(item: Item, vitals?: { maxHp: number; maxMana: number } | null): ItemChip[] {
  const tags = itemEffectTags(item);
  const chips: ItemChip[] = [];
  const restore = vitals ? potionRestoreSummary(item, vitals.maxHp, vitals.maxMana) : null;
  if (restore && restore.health > 0) chips.push({ kind: 'heal', icon: '❤', label: `Heilung ${restore.health}` });
  else if (tags.healthPercent > 0) chips.push({ kind: 'heal', icon: '❤', label: `Heilung ${potionCatalogLabel(tags.healthRestore, tags.healthPercent)}` });
  else if (tags.healthRestore > 0) chips.push({ kind: 'heal', icon: '❤', label: `Heilung ${tags.healthRestore}` });
  if (restore && restore.mana > 0) chips.push({ kind: 'mana', icon: '✨', label: `Mana ${restore.mana}` });
  else if (tags.manaPercent > 0) chips.push({ kind: 'mana', icon: '✨', label: `Mana ${potionCatalogLabel(tags.manaRestore, tags.manaPercent)}` });
  else if (tags.manaRestore > 0) chips.push({ kind: 'mana', icon: '✨', label: `Mana ${tags.manaRestore}` });
  if (tags.effectDamage !== 0) chips.push({ kind: 'damage', icon: '⚔', label: `Schaden ${tags.effectDamage}` });
  if (tags.effectMove !== 0) chips.push({ kind: 'move', icon: '➤', label: `Bewegung ${tags.effectMove}` });
  if (tags.effectRange !== 0) chips.push({ kind: 'range', icon: '🏹', label: `Reichweite ${tags.effectRange}` });
  if (isArmorBroken(item)) chips.push({ kind: 'armor', icon: '🛡', label: 'Kaputt' });
  else if (itemArmorMax(item) > 0) {
    const left = itemArmorRemaining(item);
    const full = itemArmorMax(item);
    chips.push({ kind: 'armor', icon: '🛡', label: left === full ? `Rüstung ${full}` : `Rüstung ${left}/${full}` });
  } else if (tags.effectArmor !== 0) chips.push({ kind: 'armor', icon: '🛡', label: `Rüstung ${tags.effectArmor}` });
  if (!tags.isPotion) {
    for (const stat of ATTRIBUTE_CHIPS) {
      const value = item[stat.key] || 0;
      if (!value) continue;
      chips.push({ kind: stat.kind, icon: stat.icon, label: `${stat.label} ${signedNumber(value)}` });
    }
  }
  const signedLife = parseSignedStat(item.effect, 'leben');
  if (signedLife !== 0 && !(item.health_bonus || 0)) {
    chips.push({ kind: 'life', icon: '💚', label: `Leben ${signedNumber(signedLife)}` });
  }
  const signedMana = parseSignedStat(item.effect, 'mana');
  if (signedMana !== 0) {
    chips.push({ kind: 'mana', icon: '✨', label: `Mana ${signedNumber(signedMana)}` });
  }
  return chips;
}
